import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Real-time booking channel.
 *
 *  - `bookingId`  — booking to subscribe to (null disables)
 *  - returns { lastLocation, lastStatus, sendLocation, connected }
 */
export function useBookingChannel(bookingId) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lastLocation, setLastLocation] = useState(null); // {lat, lng}
  const [lastStatus, setLastStatus] = useState(null);

  useEffect(() => {
    if (!bookingId) {
      setConnected(false);
      return undefined;
    }
    const token = localStorage.getItem('access_token');
    if (!token) return undefined;

    const httpBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsBase = httpBase.replace(/^http/, 'ws');
    const url = `${wsBase}/api/ws/booking/${bookingId}?token=${encodeURIComponent(token)}`;

    let active = true;
    let retryDelayMs = 1000;
    let timer = null;

    const open = () => {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!active) return;
        setConnected(true);
        retryDelayMs = 1000;
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'location') {
            setLastLocation({ lat: msg.lat, lng: msg.lng });
          } else if (msg.type === 'status') {
            setLastStatus(msg.status);
          }
        } catch { /* ignore */ }
      };

      ws.onclose = () => {
        setConnected(false);
        if (!active) return;
        // exponential backoff up to 30s
        timer = setTimeout(open, retryDelayMs);
        retryDelayMs = Math.min(retryDelayMs * 2, 30000);
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };

    open();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      try { wsRef.current?.close(); } catch { /* ignore */ }
      wsRef.current = null;
    };
  }, [bookingId]);

  const sendLocation = useCallback((lat, lng) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'location', lat, lng }));
    }
  }, []);

  return { connected, lastLocation, lastStatus, sendLocation };
}
